import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Description,
  LocationOn,
  AccessTime,
  Business,
  Save,
  Preview,
  Download,
  Add,
  Delete
} from '@mui/icons-material';
import api from '../services/api';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand } from '../theme/colors';

const MerchantQuoteRequestPage = () => {
  const { quoteRequestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteData, setQuoteData] = useState({
    deliveryFee: 0,
    offloadingFee: 0,
    otherCharges: 0,
    vatPercentage: 0,
    estimatedDeliveryDate: '',
    deliveryNotes: '',
    paymentTerms: 'Payment on delivery (via Eqaya Wallet)',
    merchantNotes: '',
    attachments: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    material_name: '',
    material_category: 'General',
    requested_quantity: 0,
    requested_unit: 'Units',
    specifications: '',
    brand_preference: '',
    notes: '',
    merchant_product_name: '',
    quantity_available: 0,
    unit_price: 0,
    total_price: 0,
    in_stock: true,
    lead_time_days: 0,
    availability_notes: '',
    item_notes: ''
  });

  // Catalog match suggestions (Task 10): { [quoteItemId]: [{item, score}] }
  const [suggestions, setSuggestions] = useState({});
  const [pickerFor, setPickerFor] = useState(null); // quote line item id being picked for
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);

  useEffect(() => {
    if (quoteRequestId) {
      loadQuoteRequest();
    }
  }, [quoteRequestId]);

  // Fetch catalog suggestions once the request's line items are loaded.
  useEffect(() => {
    const lines = quoteItems.map((qi) => ({
      name: qi.material_name, category: qi.material_category, unit: qi.requested_unit
    }));
    if (lines.length === 0) return;
    api.post('/merchants/catalog/match', { lines })
      .then((res) => {
        if (!res.data.success) return;
        const byItem = {};
        res.data.data.forEach((entry, idx) => {
          const qi = quoteItems[idx];
          if (qi && entry.matches.length > 0) byItem[qi.id] = entry.matches;
        });
        setSuggestions(byItem);
      })
      .catch(() => {}); // zero-catalog merchants simply see no chips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems.length]);

  const loadQuoteRequest = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/merchant-quotes/requests/${quoteRequestId}`);

      if (response.data.success) {
        const request = response.data.data;
        setQuoteRequest(request);

        // Initialize quote items from request
        if (request.request_type === 'material_form') {
          let items = [];
          
          // Parse material_request if it's a string (JSON from database)
          let materialRequest = request.material_request;
          if (typeof materialRequest === 'string') {
            try {
              materialRequest = JSON.parse(materialRequest);
            } catch (e) {
              console.error('Error parsing material_request:', e);
              materialRequest = null;
            }
          }
          
          // Handle different structures of material_request
          if (materialRequest) {
            // Structure 1: Array of categories (from MaterialRequestForm)
            if (Array.isArray(materialRequest)) {
              materialRequest.forEach(category => {
                if (category.items && Array.isArray(category.items)) {
                  category.items.forEach(item => {
                    items.push({
                      id: Date.now() + Math.random() + items.length,
                      material_name: item.materialName || item.material_name,
                      material_category: category.categoryName || category.category_name || 'General',
                      requested_quantity: item.quantity || item.requested_quantity,
                      requested_unit: item.unit || item.requested_unit,
                      specifications: item.specifications || '',
                      brand_preference: item.brandPreference || item.brand_preference || '',
                      notes: item.notes || '',
                      selected: true,
                      merchant_product_name: '',
                      quantity_available: item.quantity || item.requested_quantity || 0,
                      unit_price: 0,
                      total_price: 0,
                      in_stock: true,
                      lead_time_days: 0,
                      availability_notes: '',
                      item_notes: ''
                    });
                  });
                }
              });
            }
            // Structure 2: Object with items array (from QuoteRequestWizard)
            else if (materialRequest.items && Array.isArray(materialRequest.items)) {
              materialRequest.items.forEach(item => {
                items.push({
                  id: Date.now() + Math.random() + items.length,
                  material_name: item.materialName || item.material_name,
                  material_category: item.categoryName || item.category_name || item.material_category || 'General',
                  requested_quantity: item.quantity || item.requested_quantity,
                  requested_unit: item.unit || item.requested_unit,
                  specifications: item.specifications || '',
                  brand_preference: item.brandPreference || item.brand_preference || '',
                  notes: item.notes || '',
                  selected: true,
                  merchant_product_name: '',
                  quantity_available: item.quantity || item.requested_quantity || 0,
                  unit_price: 0,
                  total_price: 0,
                  in_stock: true,
                  lead_time_days: 0,
                  availability_notes: '',
                  item_notes: ''
                });
              });
            }
          }
          
          // If no items found but we have material_categories, create placeholder items
          if (items.length === 0 && request.material_categories && Array.isArray(request.material_categories) && request.material_categories.length > 0) {
            // Create a placeholder item for each category
            request.material_categories.forEach((category, index) => {
              items.push({
                id: Date.now() + Math.random() + index,
                material_name: `Materials from ${category}`,
                material_category: category,
                requested_quantity: 0,
                requested_unit: 'Units',
                specifications: '',
                brand_preference: '',
                notes: request.additional_notes || request.special_requirements || '',
                selected: true,
                merchant_product_name: '',
                quantity_available: 0,
                unit_price: 0,
                total_price: 0,
                in_stock: true,
                lead_time_days: 0,
                availability_notes: '',
                item_notes: ''
              });
            });
          }
          
          setQuoteItems(items);
        } else {
          // For BOQ upload, we'd need to parse the BOQ file
          // For now, create placeholder items
          setQuoteItems([]);
        }
      }
    } catch (err) {
      console.error('Error loading quote request:', err);
      setError('Failed to load quote request');
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteItem = (itemId, field, value) => {
    setQuoteItems(prev =>
      prev.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          // Recalculate total if price or quantity changed
          if (field === 'unit_price' || field === 'quantity_available') {
            updated.total_price = parseFloat(updated.unit_price || 0) * parseFloat(updated.quantity_available || 0);
          }
          return updated;
        }
        return item;
      })
    );
  };

  // Fill a quote line from a catalog item (chip tap or picker "Use"). Mirrors
  // updateQuoteItem's total_price computation exactly: unit_price * quantity_available.
  const fillFromCatalog = (quoteItemId, catalogItem) => {
    setQuoteItems((prev) => prev.map((qi) => {
      if (qi.id !== quoteItemId) return qi;
      const unitPrice = parseFloat(catalogItem.unit_price || 0);
      const quantityAvailable = catalogItem.available_quantity ?? qi.quantity_available;
      return {
        ...qi,
        merchant_product_name: catalogItem.name,
        unit_price: unitPrice,
        in_stock: catalogItem.in_stock !== false,
        quantity_available: quantityAvailable,
        total_price: unitPrice * parseFloat(quantityAvailable || 0)
      };
    }));
  };

  const toggleItemSelection = (itemId) => {
    setQuoteItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const calculateTotals = () => {
    const selectedItems = quoteItems.filter(item => item.selected);
    const subtotal = selectedItems.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0);
    const vatAmount = quoteData.vatPercentage ? (subtotal * parseFloat(quoteData.vatPercentage) / 100) : 0;
    const totalBeforeVat = subtotal + parseFloat(quoteData.deliveryFee || 0) +
                  parseFloat(quoteData.offloadingFee || 0) +
                  parseFloat(quoteData.otherCharges || 0);
    const total = totalBeforeVat + vatAmount;

    return { subtotal, vatAmount, totalBeforeVat, total };
  };

  const [draftSaving, setDraftSaving] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file later
    if (files.length === 0) return;

    try {
      setAttachmentUploading(true);
      const uploaded = [];
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          setError(`${file.name} is larger than 10MB and was skipped`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'quote_attachment');
        const response = await api.post('/uploads/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          uploaded.push({ name: file.name, url: response.data.data.url });
        }
      }
      if (uploaded.length > 0) {
        setQuoteData(prev => ({
          ...prev,
          attachments: [...(prev.attachments || []), ...uploaded]
        }));
      }
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError('Failed to upload attachment. Please try again.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  // Load existing draft when page loads
  useEffect(() => {
    if (quoteRequestId && !draftLoaded) {
      loadDraft();
    }
  }, [quoteRequestId, draftLoaded]);

  const loadDraft = async () => {
    try {
      const response = await api.get(`/merchant-quotes/draft/${quoteRequestId}`);
      if (response.data.success && response.data.data) {
        const draft = response.data.data;
        // Restore quote data from draft
        setQuoteData(prev => ({
          ...prev,
          deliveryFee: draft.delivery_fee || 0,
          offloadingFee: draft.offloading_fee || 0,
          otherCharges: draft.other_charges || 0,
          vatPercentage: draft.vat_percentage || 0,
          estimatedDeliveryDate: draft.estimated_delivery_date ? draft.estimated_delivery_date.substring(0, 10) : '',
          deliveryNotes: draft.delivery_notes || '',
          paymentTerms: draft.payment_terms || prev.paymentTerms,
          merchantNotes: draft.merchant_notes || '',
          attachments: draft.attachments ? (typeof draft.attachments === 'string' ? JSON.parse(draft.attachments) : draft.attachments) : []
        }));

        // Restore items from draft if available
        const savedItems = draft.saved_items;
        if (savedItems && Array.isArray(savedItems) && savedItems.length > 0) {
          setQuoteItems(prev => {
            // Merge draft items onto existing request items by material_name
            return prev.map(reqItem => {
              const draftItem = savedItems.find(si => si.material_name === reqItem.material_name);
              if (draftItem) {
                return {
                  ...reqItem,
                  merchant_product_name: draftItem.merchant_product_name || '',
                  quantity_available: draftItem.quantity_available || reqItem.quantity_available,
                  unit_price: parseFloat(draftItem.unit_price || 0),
                  total_price: parseFloat(draftItem.total_price || 0),
                  in_stock: draftItem.in_stock !== false,
                  lead_time_days: draftItem.lead_time_days || 0,
                  availability_notes: draftItem.availability_notes || '',
                  item_notes: draftItem.item_notes || '',
                  selected: true
                };
              }
              return reqItem;
            });
          });
        }
      }
    } catch (err) {
      // Draft load failure is non-critical
      console.log('No existing draft found');
    } finally {
      setDraftLoaded(true);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setDraftSaving(true);
      setError('');

      const selectedItems = quoteItems.filter(item => item.selected);

      await api.post('/merchant-quotes/draft', {
        quoteRequestId,
        quoteItems: selectedItems.map(item => ({
          material_name: item.material_name,
          material_category: item.material_category,
          requested_quantity: item.requested_quantity,
          requested_unit: item.requested_unit,
          merchant_product_name: item.merchant_product_name,
          quantity_available: item.quantity_available,
          unit_price: item.unit_price,
          total_price: item.total_price,
          in_stock: item.in_stock,
          lead_time_days: item.lead_time_days,
          availability_notes: item.availability_notes,
          specifications: item.specifications,
          brand_preference: item.brand_preference,
          item_notes: item.item_notes
        })),
        deliveryFee: quoteData.deliveryFee,
        offloadingFee: quoteData.offloadingFee,
        otherCharges: quoteData.otherCharges,
        vatPercentage: quoteData.vatPercentage,
        estimatedDeliveryDate: quoteData.estimatedDeliveryDate,
        deliveryNotes: quoteData.deliveryNotes,
        paymentTerms: quoteData.paymentTerms,
        merchantNotes: quoteData.merchantNotes,
        attachments: quoteData.attachments
      });

      // Show success briefly
      setError('');
      alert('Draft saved successfully! You can return to finish this quote later.');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setDraftSaving(false);
    }
  };

  const handleSubmitQuote = async () => {
    try {
      setSaving(true);
      setError('');

      const selectedItems = quoteItems.filter(item => item.selected);
      if (selectedItems.length === 0) {
        setError('Please quote at least one item');
        return;
      }

      const { subtotal, vatAmount, total } = calculateTotals();

      const response = await api.post('/merchant-quotes', {
        quoteRequestId,
        quoteItems: selectedItems.map(item => ({
          material_name: item.material_name,
          material_category: item.material_category,
          requested_quantity: item.requested_quantity,
          requested_unit: item.requested_unit,
          merchant_product_name: item.merchant_product_name,
          quantity_available: item.quantity_available,
          unit_price: item.unit_price,
          total_price: item.total_price,
          in_stock: item.in_stock,
          lead_time_days: item.lead_time_days,
          availability_notes: item.availability_notes,
          specifications: item.specifications,
          brand_preference: item.brand_preference,
          item_notes: item.item_notes
        })),
        deliveryFee: quoteData.deliveryFee,
        offloadingFee: quoteData.offloadingFee,
        otherCharges: quoteData.otherCharges,
        vatPercentage: quoteData.vatPercentage,
        estimatedDeliveryDate: quoteData.estimatedDeliveryDate,
        deliveryNotes: quoteData.deliveryNotes,
        paymentTerms: quoteData.paymentTerms,
        merchantNotes: quoteData.merchantNotes,
        attachments: quoteData.attachments
      });

      if (response.data.success) {
        // Success - navigate back or show success message
        navigate('/dashboard');
      } else {
        setError(response.data.error || 'Failed to submit quote');
      }
    } catch (err) {
      console.error('Error submitting quote:', err);
      setError(err.response?.data?.error || 'Failed to submit quote. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!quoteRequest) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Quote request not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const { subtotal, vatAmount, totalBeforeVat, total } = calculateTotals();
  const selectedItemsCount = quoteItems.filter(item => item.selected).length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Quote Request #{quoteRequestId}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Client Information */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Client Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Client Name:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {quoteRequest.client_name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Project:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {quoteRequest.project_title || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Delivery Address:</Typography>
            <Typography variant="body1">
              {quoteRequest.delivery_address?.city || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Delivery Timeframe:</Typography>
            <Typography variant="body1">
              {quoteRequest.delivery_timeframe || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {quoteRequest.boq_file_url && (
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<Download />}
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={() => {
                const link = document.createElement('a');
                link.href = quoteRequest.boq_file_url;
                link.download = quoteRequest.boq_file_name || 'BOQ.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download BOQ File
            </Button>
            <Button
              startIcon={<Description />}
              variant="text"
              onClick={() => window.open(quoteRequest.boq_file_url, '_blank')}
            >
              View in New Tab
            </Button>
          </Box>
        )}
      </Paper>

      {/* Material Request Items */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Prepare Your Quote
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {quoteRequest.request_type === 'boq_upload' 
                ? 'Download the BOQ file above, review it, and manually add items you can quote below.'
                : 'Quote all items or select which ones you can supply:'}
            </Typography>
          </Box>
          {quoteRequest.request_type === 'boq_upload' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddItemDialogOpen(true)}
            >
              Add Item from BOQ
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Quote</TableCell>
                <TableCell>Material/Product</TableCell>
                <TableCell>Client Specifications</TableCell>
                <TableCell>Qty Requested</TableCell>
                <TableCell>Your Product</TableCell>
                <TableCell>Qty Available</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total</TableCell>
                  <TableCell>Stock Status</TableCell>
                  {quoteRequest.request_type === 'boq_upload' && (
                    <TableCell>Actions</TableCell>
                  )}
              </TableRow>
            </TableHead>
            <TableBody>
              {quoteItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={item.selected}
                      onChange={() => toggleItemSelection(item.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.material_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.material_category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(item.specifications || item.brand_preference || item.notes) ? (
                      <Box>
                        {item.specifications && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Specs:</strong> {item.specifications}
                          </Typography>
                        )}
                        {item.brand_preference && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Brand:</strong> {item.brand_preference}
                          </Typography>
                        )}
                        {item.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            <strong>Notes:</strong> {item.notes}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No specifications provided
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.requested_quantity} {item.requested_unit}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.merchant_product_name}
                      onChange={(e) => updateQuoteItem(item.id, 'merchant_product_name', e.target.value)}
                      placeholder="Your product name"
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}>
                      {(suggestions[item.id] || []).map((m) => (
                        <Chip
                          key={m.item.id}
                          size="small"
                          clickable
                          variant="outlined"
                          color="primary"
                          label={`${m.item.name} — $${parseFloat(m.item.unit_price).toFixed(2)}/${m.item.unit}`}
                          onClick={() => fillFromCatalog(item.id, m.item)}
                        />
                      ))}
                      <Button
                        size="small"
                        onClick={() => { setPickerFor(item.id); setPickerSearch(''); setPickerResults([]); }}
                      >
                        Browse catalog
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantity_available}
                      onChange={(e) => updateQuoteItem(item.id, 'quantity_available', e.target.value)}
                      disabled={!item.selected}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateQuoteItem(item.id, 'unit_price', e.target.value)}
                      disabled={!item.selected}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${parseFloat(item.total_price || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={item.in_stock ? 'in_stock' : 'out_of_stock'}
                        onChange={(e) => updateQuoteItem(item.id, 'in_stock', e.target.value === 'in_stock')}
                        disabled={!item.selected}
                      >
                        <MenuItem value="in_stock">In Stock</MenuItem>
                        <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  {quoteRequest.request_type === 'boq_upload' && (
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setQuoteItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {quoteItems.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {quoteRequest.request_type === 'boq_upload' 
              ? 'No items added yet. Download the BOQ file above, review it, and click "Add Item from BOQ" to start adding items manually.'
              : 'No items to quote. If this is a BOQ upload, please review the uploaded file.'}
          </Alert>
        )}
      </Paper>

      {/* Delivery & Charges */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Delivery & Charges
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Delivery Fee"
              type="number"
              value={quoteData.deliveryFee}
              onChange={(e) => setQuoteData({ ...quoteData, deliveryFee: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Offloading Fee"
              type="number"
              value={quoteData.offloadingFee}
              onChange={(e) => setQuoteData({ ...quoteData, offloadingFee: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Other Charges"
              type="number"
              value={quoteData.otherCharges}
              onChange={(e) => setQuoteData({ ...quoteData, otherCharges: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Estimated Delivery Date"
              value={quoteData.estimatedDeliveryDate}
              onChange={(e) => setQuoteData({ ...quoteData, estimatedDeliveryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Delivery Notes"
              value={quoteData.deliveryNotes}
              onChange={(e) => setQuoteData({ ...quoteData, deliveryNotes: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Payment Terms</InputLabel>
              <Select
                value={quoteData.paymentTerms}
                label="Payment Terms"
                onChange={(e) => setQuoteData({ ...quoteData, paymentTerms: e.target.value })}
              >
                <MenuItem value="Payment on delivery (via Eqaya Wallet)">
                  Payment on delivery (via Eqaya Wallet)
                </MenuItem>
                <MenuItem value="50% deposit, balance on delivery">
                  50% deposit, balance on delivery
                </MenuItem>
                <MenuItem value="Full payment upfront">
                  Full payment upfront
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Quote Summary */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Quote Summary
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Items Quoted: {selectedItemsCount} of {quoteItems.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Subtotal (Materials):</Typography>
          <Typography sx={{ fontWeight: 600 }}>${subtotal.toFixed(2)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Delivery Fee:</Typography>
          <Typography>${parseFloat(quoteData.deliveryFee || 0).toFixed(2)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Offloading Fee:</Typography>
          <Typography>${parseFloat(quoteData.offloadingFee || 0).toFixed(2)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>Other Charges:</Typography>
          <Typography>${parseFloat(quoteData.otherCharges || 0).toFixed(2)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>TOTAL QUOTE:</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ${totalBeforeVat.toFixed(2)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Checkbox
            checked={quoteData.vatPercentage > 0}
            onChange={(e) => setQuoteData({ 
              ...quoteData, 
              vatPercentage: e.target.checked ? 15 : 0 
            })}
          />
          <Typography>Add 15% VAT</Typography>
        </Box>
        {quoteData.vatPercentage > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography>VAT ({quoteData.vatPercentage}%):</Typography>
            <Typography>${vatAmount.toFixed(2)}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>GRAND TOTAL:</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            ${total.toFixed(2)}
          </Typography>
        </Box>
      </Paper>

      {/* Additional Notes */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Additional Notes to Client (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={quoteData.merchantNotes}
          onChange={(e) => setQuoteData({ ...quoteData, merchantNotes: e.target.value })}
          placeholder="All materials are high quality. Cement is fresh stock from last week..."
          inputProps={{ maxLength: 1000 }}
          helperText={`${quoteData.merchantNotes.length}/1000 characters`}
        />

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label" disabled={attachmentUploading}>
            {attachmentUploading ? 'Uploading…' : 'Attach Files (spec sheets, certificates)'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleAttachmentUpload}
            />
          </Button>
          {(quoteData.attachments || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {quoteData.attachments.map((att, idx) => (
                <Chip
                  key={idx}
                  label={att.name || `Attachment ${idx + 1}`}
                  component="a"
                  href={att.url}
                  target="_blank"
                  clickable
                  onDelete={() => setQuoteData(prev => ({
                    ...prev,
                    attachments: prev.attachments.filter((_, i) => i !== idx)
                  }))}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={draftSaving ? <CircularProgress size={16} /> : <Save />}
          onClick={handleSaveDraft}
          disabled={saving || draftSaving}
        >
          {draftSaving ? 'Saving...' : 'Save as Draft'}
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Preview />}
            onClick={() => setPreviewOpen(true)}
          >
            Preview Quote
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSubmitQuote}
            disabled={saving || selectedItemsCount === 0}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            {saving ? 'Submitting...' : 'Submit Quote to Client'}
          </Button>
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setPreviewOpen(false)} />
        <DialogTitle>Quote Preview</DialogTitle>
        <DialogContent>
          <QuotePreview quoteData={quoteData} quoteItems={quoteItems.filter(i => i.selected)} totals={calculateTotals()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialogOpen} onClose={() => setAddItemDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setAddItemDialogOpen(false)} />
        <DialogTitle>Add Item from BOQ</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Material Name *"
                  value={newItem.material_name}
                  onChange={(e) => setNewItem({ ...newItem, material_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={newItem.material_category}
                  onChange={(e) => setNewItem({ ...newItem, material_category: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Requested Quantity *"
                  type="number"
                  value={newItem.requested_quantity}
                  onChange={(e) => setNewItem({ ...newItem, requested_quantity: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Unit *"
                  value={newItem.requested_unit}
                  onChange={(e) => setNewItem({ ...newItem, requested_unit: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Your Product Name"
                  value={newItem.merchant_product_name}
                  onChange={(e) => setNewItem({ ...newItem, merchant_product_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Quantity Available *"
                  type="number"
                  value={newItem.quantity_available}
                  onChange={(e) => {
                    const qty = e.target.value;
                    setNewItem({ 
                      ...newItem, 
                      quantity_available: qty,
                      total_price: parseFloat(qty || 0) * parseFloat(newItem.unit_price || 0)
                    });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Unit Price *"
                  type="number"
                  value={newItem.unit_price}
                  onChange={(e) => {
                    const price = e.target.value;
                    setNewItem({ 
                      ...newItem, 
                      unit_price: price,
                      total_price: parseFloat(newItem.quantity_available || 0) * parseFloat(price || 0)
                    });
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Stock Status</InputLabel>
                  <Select
                    value={newItem.in_stock ? 'in_stock' : 'out_of_stock'}
                    onChange={(e) => setNewItem({ ...newItem, in_stock: e.target.value === 'in_stock' })}
                    label="Stock Status"
                  >
                    <MenuItem value="in_stock">In Stock</MenuItem>
                    <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Specifications"
                  multiline
                  rows={2}
                  value={newItem.specifications}
                  onChange={(e) => setNewItem({ ...newItem, specifications: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Brand Preference"
                  value={newItem.brand_preference}
                  onChange={(e) => setNewItem({ ...newItem, brand_preference: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lead Time (Days)"
                  type="number"
                  value={newItem.lead_time_days}
                  onChange={(e) => setNewItem({ ...newItem, lead_time_days: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Availability Notes"
                  multiline
                  rows={2}
                  value={newItem.availability_notes}
                  onChange={(e) => setNewItem({ ...newItem, availability_notes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Item Notes"
                  multiline
                  rows={2}
                  value={newItem.item_notes}
                  onChange={(e) => setNewItem({ ...newItem, item_notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!newItem.material_name || !newItem.requested_quantity || !newItem.requested_unit || 
                  !newItem.quantity_available || !newItem.unit_price) {
                setError('Please fill in all required fields');
                return;
              }
              const item = {
                ...newItem,
                id: Date.now() + Math.random(),
                selected: true,
                total_price: parseFloat(newItem.quantity_available || 0) * parseFloat(newItem.unit_price || 0)
              };
              setQuoteItems([...quoteItems, item]);
              setNewItem({
                material_name: '',
                material_category: 'General',
                requested_quantity: 0,
                requested_unit: 'Units',
                specifications: '',
                brand_preference: '',
                notes: '',
                merchant_product_name: '',
                quantity_available: 0,
                unit_price: 0,
                total_price: 0,
                in_stock: true,
                lead_time_days: 0,
                availability_notes: '',
                item_notes: ''
              });
              setAddItemDialogOpen(false);
            }}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Catalog Picker Dialog (Task 10) */}
      <Dialog open={!!pickerFor} onClose={() => setPickerFor(null)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setPickerFor(null)} />
        <DialogTitle>Insert from your catalog</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 1, mb: 2 }}
            placeholder="Search your products…"
            value={pickerSearch}
            onChange={async (e) => {
              setPickerSearch(e.target.value);
              try {
                const res = await api.get('/merchants/catalog', { params: { search: e.target.value, limit: 20 } });
                if (res.data.success) setPickerResults(res.data.data || []);
              } catch {
                setPickerResults([]);
              }
            }}
          />
          {pickerResults.map((ci) => (
            <Box
              key={ci.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{ci.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ${parseFloat(ci.unit_price).toFixed(2)} / {ci.unit}{ci.in_stock ? '' : ' — out of stock'}
                </Typography>
              </Box>
              <Button size="small" onClick={() => { fillFromCatalog(pickerFor, ci); setPickerFor(null); }}>
                Use
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Quote Preview Component
const QuotePreview = ({ quoteData, quoteItems, totals }) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Quote Items</Typography>
      {quoteItems.map((item, index) => (
        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.material_name}</Typography>
          <Typography variant="body2">Qty: {item.quantity_available} {item.requested_unit}</Typography>
          <Typography variant="body2">Unit Price: ${parseFloat(item.unit_price).toFixed(2)}</Typography>
          <Typography variant="body2">Total: ${parseFloat(item.total_price).toFixed(2)}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Total:</Typography>
        <Typography variant="h6">${totals.total.toFixed(2)}</Typography>
      </Box>
    </Box>
  );
};

export default MerchantQuoteRequestPage;

